import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import {
  importInput__labelStyles,
  importInput__nativeInputStyles,
  importInput__successContainerStyles,
  importInput__fileNameStyles,
  importInput__fileInfoStyles,
  importInput__dropZoneStyles,
  importInput__dropZoneNormalStyles,
  importInput__dropZoneDraggingStyles,
  importInput__enhancedLabelStyles,
  importInput__uploadIconStyles,
  importInput__uploadDescStyles,
  importInput__uploadDescNormalStyles,
  importInput__uploadDescDraggingStyles,
  importInput__acceptedFormatsStyles,
  importInput__dropInstructionStyles,
} from './Styles'
import { Button, Flexbox, Icon, Spacer, Text } from '@common'
import { formatBytes } from '@utils/basic'

interface ImportInputProps {
  acceptedFormatsDesc?: string
  uploadDesc?: React.ReactNode
  onChange: any
  onRemove: () => void
  file: File
  displayName?: string
}

const ImportInput: React.FC<ImportInputProps> = ({
  onChange,
  onRemove,
  uploadDesc,
  acceptedFormatsDesc,
  file,
  displayName,
}) => {
  const [css] = useStyletron()
  const [isDragging, setIsDragging] = useState(false)
  const dropRef = useRef<HTMLDivElement | null>(null)

  const handleDrag = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragIn = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragOut = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const rect = dropRef.current?.getBoundingClientRect()
    if (
      rect &&
      (e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom)
    ) {
      setIsDragging(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        onChange({ target: { files: e.dataTransfer.files } })
        e.dataTransfer.clearData()
      }
    },
    [onChange]
  )

  useEffect(() => {
    const div = dropRef.current
    if (div) {
      div.addEventListener('dragenter', handleDragIn)
      div.addEventListener('dragleave', handleDragOut)
      div.addEventListener('dragover', handleDrag)
      div.addEventListener('drop', handleDrop)

      return () => {
        div.removeEventListener('dragenter', handleDragIn)
        div.removeEventListener('dragleave', handleDragOut)
        div.removeEventListener('dragover', handleDrag)
        div.removeEventListener('drop', handleDrop)
      }
    }
  }, [handleDrag, handleDragIn, handleDragOut, handleDrop])

  if (file) {
    const lastModified = new Date(file.lastModified)

    return (
      <div className={css(importInput__successContainerStyles)}>
        <Flexbox
          justifyContent="flex-start"
          alignItems="center"
          gap={12}
          flexDirection="row"
        >
          <Icon name="CheckCircle" appearance="primary" size="l" />
          <Flexbox flexDirection="column" gap={4}>
            <Text className={css(importInput__fileNameStyles)}>
              {displayName || file.name}
            </Text>
            <Text light className={css(importInput__fileInfoStyles)}>
              {formatBytes(file.size)} • {lastModified.toLocaleDateString()}
            </Text>
          </Flexbox>
          <Flexbox
            justifyContent="center"
            alignItems="center"
            gap={8}
            style={{ marginLeft: 'auto' }}
          >
            <Button intent="danger" appearance="default" onClick={() => onRemove()}>
              Remove
            </Button>
          </Flexbox>
        </Flexbox>
      </div>
    )
  }

  return (
    <>
      <div
        ref={dropRef}
        className={css({
          ...importInput__dropZoneStyles,
          ...(isDragging
            ? importInput__dropZoneDraggingStyles
            : importInput__dropZoneNormalStyles),
        })}
      >
        <input
          type="file"
          onChange={onChange}
          name="importData"
          id="importData"
          className={css(importInput__nativeInputStyles)}
        />
        <label
          htmlFor="importData"
          className={css({
            ...importInput__labelStyles,
            ...importInput__enhancedLabelStyles,
          })}
        >
          <Flexbox justifyContent="center" flexDirection="column" alignItems="center">
            <Icon
              name="Upload"
              appearance="light"
              size="xl"
              className={css(importInput__uploadIconStyles)}
            />
            <Spacer y="large" />
            <Text
              light
              className={css({
                ...importInput__uploadDescStyles,
                ...(isDragging
                  ? importInput__uploadDescDraggingStyles
                  : importInput__uploadDescNormalStyles),
              })}
            >
              {uploadDesc}
            </Text>
            <Spacer y="default" />
            <Text light className={css(importInput__acceptedFormatsStyles)}>
              {acceptedFormatsDesc}
            </Text>
            <Spacer y="default" />
            <Text light className={css(importInput__dropInstructionStyles)}>
              {isDragging ? 'Drop your file here' : 'or drag and drop'}
            </Text>
          </Flexbox>
        </label>
      </div>
    </>
  )
}

export default ImportInput
